import { Box, IconButton, Paper, styled, Typography } from "@mui/material";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import { useAppContextController } from "context/AppContext";
import { useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation } from "react-query";
import { useSnackbar } from "notistack";

import CustomStepper from "components/CustomizedMui/CustomStepper";
import PictureAvatar from "components/PictureAvatar";
import FlexWrapper from "components/Wrapper/FlexWrapper";
import FileDropzone from "components/Dropzone";
import { useNewApplicantContext } from "context/NewApplicantContext";
import { ONBOARDING_OBJECTS_ENUM } from "utils/constants/applicant";
import { getCompanyImageUrl } from "utils/helpers/upload";
import { baseAxios } from "config";
import UnsavedChangesModal from "../NewApplicantForms/UnsavedChangesModal";

const StyledImg = styled("img")(() => ({
  borderRadius: "15px",
}));

const NewApplicantHeader = ({ isAvailable, setIsAvailable }) => {
  const { applicantCode } = useParams();
  const {
    activeStep,
    activeStepId,
    initializeApplicant,
    applicant,
    setActiveStep,
    currentFormState,
    registrationSteps,
    updateApplicantAction,
  } = useNewApplicantContext();
  const { company } = useAppContextController();

  const IMAGE_SERVER = getCompanyImageUrl(company);

  const [clickDirection, setClickDirection] = useState(null);
  const [openUnsavedChangesModal, setOpenUnsavedChangesModal] = useState(false);
  const [stepToGoId, setStepToGoId] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  const fileInputRef = useRef(null);

  const uploadImgMutation = useMutation(
    async (imgForm) => {
      await baseAxios.post(
        `/outside-protected/upload/applicants/${applicant._id}/${imgForm.type}`,
        imgForm.form
      );
      return imgForm.data;
    },
    {
      onSuccess: (data) => {
        updateApplicantAction(applicant._id, data);
        enqueueSnackbar("Profile image has been updated!", { variant: "success" });
      },
      onError: (error) => {
        enqueueSnackbar(`Failed to update profile image: ${error.toString()}`, {
          variant: "error",
        });
      },
    }
  );

  const handleFileSelect = (file) => {
    if (!applicant?._id) {
      enqueueSnackbar("Applicant not found. Please refresh the page.", { variant: "error" });
      return;
    }

    // Validate file type
    if (!file.type.startsWith("image/")) {
      enqueueSnackbar("Please select a valid image file.", { variant: "error" });
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      enqueueSnackbar("Image size must be less than 5MB.", { variant: "error" });
      return;
    }

    const form = new FormData();
    form.append("file", file);

    uploadImgMutation.mutate({
      form,
      data: { profileImg: file.name },
      type: "photo",
    });
  };

  const onDropImageHandler = (img) => {
    const file = img[0];
    handleFileSelect(file);
  };

  const handleCameraClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileInputChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  // useEffect(() => {
  //   const initialize = async () => {
  //     const res = await initializeApplicant(applicantCode);
  //     if (res) setIsAvailable(true);
  //     else setIsAvailable(false);
  //   };
  //   initialize();
  // }, [applicantCode]);

  const fullName = useMemo(
    () => `${applicant.firstName || ""} ${applicant.lastName || ""}`,
    [applicant]
  );

  const handleStepClick = (stepData) => {
    const { id, isActive } = stepData;
    if (activeStep === ONBOARDING_OBJECTS_ENUM.COMPLETE) {
      return;
    }
    if (currentFormState?.isDirty) {
      setClickDirection("setStep");
      setOpenUnsavedChangesModal(true);
      setStepToGoId(id);
    } else if (isActive) {
      setActiveStep(id);
    }
  };

  const { company: primaryCompany, setCompany: setPrimaryCompany } = useAppContextController();

  return (
    <Box position="relative">
      <a href="/">
        <StyledImg
          src={`${getCompanyImageUrl(primaryCompany)}/company/${primaryCompany?.slug}/banner/${
            primaryCompany?.bannerUrl
          }`}
          alt=""
          width="100%"
        />
      </a>
      {isAvailable && activeStep !== "verification" ? (
        <Paper elevation={3} sx={{ px: "40px", py: "20px" }}>
          <FlexWrapper
            alignItems={{ xs: "normal", lg: "center" }}
            justifyContent={{ xs: "center", lg: "space-between" }}
            flexDirection={{ xs: "column", lg: "row" }}
          >
            <Box
              sx={(theme) => ({
                flex: 0.3,
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: "20px",
                justifyContent: { xs: "center", md: "flex-start" },
                [theme.breakpoints.down("lg")]: {
                  flex: 1,
                },
              })}
            >
              <Box sx={{ position: "relative", cursor: "pointer" }}>
                <FileDropzone
                  onDrop={onDropImageHandler}
                  avatar={
                    <PictureAvatar
                      image={
                        applicant?.profileImg
                          ? `${IMAGE_SERVER}/applicants/${applicant?._id}/photo/${applicant?.profileImg}`
                          : null
                      }
                      firstName={applicant?.firstName}
                      lastName={applicant?.lastName}
                      userId={applicant?._id}
                      size="lg"
                      disableHover
                    />
                  }
                  message="Click or drag to update photo"
                  hideMessage
                  customStyles={{
                    minHeight: "auto",
                    border: "none",
                    backgroundColor: "transparent",
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                      borderRadius: "50%",
                    },
                  }}
                />
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileInputChange}
                  style={{ display: "none" }}
                />
                <IconButton
                  onClick={handleCameraClick}
                  sx={{
                    position: "absolute",
                    bottom: -5,
                    right: -5,
                    backgroundColor: "grey.600",
                    color: "white",
                    width: 30,
                    height: 30,
                    "&:hover": {
                      backgroundColor: "grey.900",
                    },
                  }}
                  size="small"
                >
                  <CameraAltIcon fontSize="small" color="white" />
                </IconButton>
              </Box>
              <Box>
                <Typography variant="h4">{fullName}</Typography>
                <Typography>Applicant User</Typography>
              </Box>
            </Box>
            <Box
              maxWidth="100%"
              sx={(theme) => ({
                overflowY: "auto",
                flex: 0.7,
                display: "flex",
                [theme.breakpoints.down("lg")]: {
                  flex: 1,
                },
              })}
            >
              <CustomStepper activeStepId={activeStepId} steps={registrationSteps} />
            </Box>
          </FlexWrapper>
        </Paper>
      ) : null}
      <UnsavedChangesModal
        clickDirection={clickDirection}
        openUnsavedChangesModal={openUnsavedChangesModal}
        setOpenUnsavedChangesModal={setOpenUnsavedChangesModal}
        stepToGoId={stepToGoId}
      />
    </Box>
  );
};

export default NewApplicantHeader;
